import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { mediaUrl } from "../../lib/api";

const ytId = (url = "") => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

function VideoCard({ video }) {
  const [play, setPlay] = useState(false);
  const id = ytId(video.youtube_url);
  if (!id) return null;
  return (
    <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="experience-video">
      {play ? (
        <iframe
          className="h-full w-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1`}
          title={video.title}
          allow="accelerated-media; autoplay; encrypted-media"
          allowFullScreen
        />
      ) : (
        <button onClick={() => setPlay(true)} className="group h-full w-full" data-testid="experience-video-play">
          <img src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`} alt={video.title} className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-jauja-lime text-jauja-bg transition-transform duration-300 group-hover:scale-110">
              <Play size={26} fill="currentColor" />
            </span>
          </span>
          <span className="absolute bottom-4 left-4 text-sm font-medium text-white">{video.title}</span>
        </button>
      )}
    </div>
  );
}

export default function Experience({ experience }) {
  const [hovered, setHovered] = useState(null);
  const gallery = experience.gallery || [];

  return (
    <section id="experiencia" className="relative py-28 sm:py-36" data-testid="experience-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>{experience.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-medium tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl">
              {experience.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg leading-relaxed text-white/70">{experience.body}</p>
          </Reveal>
        </div>

        {(experience.videos || []).length > 0 && (
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {experience.videos.map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <VideoCard video={v} />
              </Reveal>
            ))}
          </div>
        )}

        {gallery.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="experience-gallery">
            {gallery.map((img, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <motion.div
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  animate={{
                    opacity: hovered === null || hovered === i ? 1 : 0.4,
                    scale: hovered === i ? 1.02 : hovered === null ? 1 : 0.98,
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/10"
                >
                  <img src={mediaUrl(img)} alt="Experiencia Jauja" className="h-full w-full object-cover" />
                </motion.div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
